import * as React from 'react';
import { history, request } from '@umijs/max';
import { Button, Result } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { getAccessReceivedAt, getSubscriptionAccess, setSubscriptionAccess } from './access';
import { ApplicationAccess, remainingAccessMs } from './subscription';

/** Children never mount while access is denied or cannot be verified. */
export default function ApplicationAccessGate({ currentUser, children }: { currentUser: any; children: ReactNode }) {
  const access: ApplicationAccess | undefined = currentUser?.application_access;
  const [unavailable, setUnavailable] = useState(false);
  const expired = access?.allowed && remainingAccessMs(access, getAccessReceivedAt()) === 0;
  const blocked = access?.allowed === false || expired;

  useEffect(() => {
    if (!currentUser) return;
    if (blocked) {
      if (expired) setSubscriptionAccess({ ...access!, allowed: false, reason: 'expired' });
      else history.replace('/subscription-required');
      return;
    }
    // Older API versions have no access snapshot during the frontend-first rollout.
    if (!access) return;
    let disposed = false;
    let checking = false;
    let timer: ReturnType<typeof setTimeout>;
    const expire = () => {
      if (disposed) return;
      const remaining = remainingAccessMs(access, getAccessReceivedAt());
      if (remaining <= 0) setSubscriptionAccess({ ...access, allowed: false, reason: 'expired' });
      else timer = setTimeout(expire, Math.min(remaining, 2_147_000_000));
    };
    const recheck = async () => {
      if (checking || document.visibilityState === 'hidden') return;
      if (remainingAccessMs(access, getAccessReceivedAt()) <= 0) { expire(); return; }
      checking = true;
      try {
        const next = await request<ApplicationAccess>('/user/application-access', { skipErrorHandler: true });
        if (!disposed) { setUnavailable(false); setSubscriptionAccess(next); }
      } catch (error: any) {
        if (!disposed && error.code !== 'ERR_CANCELED') setUnavailable(true);
      } finally { checking = false; }
    };
    expire();
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      disposed = true;
      clearTimeout(timer);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [access, currentUser?.id, blocked, expired]);

  if (!currentUser || blocked || getSubscriptionAccess()?.allowed === false) return null;
  if (unavailable) return <Result status="warning" title="Access could not be verified" subTitle="Please try again in a moment."
    extra={<Button onClick={() => window.location.reload()}>Retry</Button>} />;
  return <>{children}</>;
}
